import { useEffect, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import GameScreenLayout from "@/components/GameScreenLayout"
import { ColorRank, DeckSearchMode } from "@/types/battle"
import {
  connectBattleSocket,
  emitBattleEvent,
  emitBattleEventWhenReady,
  onBattleEvent,
} from "@/socket/battleSocket"
import { buildDeckRadarSearchDto, buildDeckRadarStatusRequest, formatDeckSearchModeLabel } from "@/utils/deckSearch"
import { normalizeDeckRadarStatus } from "@/utils/deckPayload"
import { useAuth } from "@/context/AuthContext"

export default function DeckMatchmakingPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const { token } = useAuth()

  const deckSearchMode = (params.get("deckSearchMode") as DeckSearchMode) || "RANK"
  const colorRank = (params.get("colorRank") as ColorRank) || "WHITE"
  const cardIds = (params.get("cardIds") ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
  const autoStart = params.get("autoStart") === "1" || params.get("autoStart") === "true"

  const [status, setStatus] = useState("Подключение…")
  const [searching, setSearching] = useState(false)
  const autoStartedRef = useRef(false)

  useEffect(() => {
    if (cardIds.length !== 6) {
      setStatus("Нужно 6 cardIds в URL")
      return
    }

    void connectBattleSocket(token).catch(() => {
      setStatus("Нет battle socket")
    })

    const offStarted = onBattleEvent("deck-battle-started", (payload) => {
      navigate("/play/deck-duel", { state: { battle: payload }, replace: true })
    })
    const offStatus = onBattleEvent("deck-radar-status", (raw) => {
      const s = normalizeDeckRadarStatus(raw)
      if (s) {
        setStatus(
          `Очередь ${s.queueSize}, матчей ${s.matchableCount}, энергия ${s.energyCost}`
        )
      }
    })

    const poll = window.setInterval(() => {
      emitBattleEvent(
        "deck-radar-status",
        buildDeckRadarStatusRequest(deckSearchMode, colorRank)
      )
    }, 7000)

    return () => {
      offStarted()
      offStatus()
      window.clearInterval(poll)
      emitBattleEvent("deck-radar-cancel")
    }
  }, [token, navigate, deckSearchMode, colorRank, cardIds.length])

  useEffect(() => {
    if (!autoStart || autoStartedRef.current || cardIds.length !== 6) return
    autoStartedRef.current = true
    setSearching(true)
    void emitBattleEventWhenReady(
      token,
      "deck-radar-search",
      buildDeckRadarSearchDto(deckSearchMode, cardIds, colorRank)
    ).then((ok) => {
      if (!ok) setStatus("Сокет не готов")
      setSearching(false)
    })
  }, [autoStart, cardIds, deckSearchMode, colorRank, token])

  function search() {
    if (cardIds.length !== 6) return
    setSearching(true)
    void emitBattleEventWhenReady(
      token,
      "deck-radar-search",
      buildDeckRadarSearchDto(deckSearchMode, cardIds, colorRank)
    ).finally(() => setSearching(false))
  }

  function cancel() {
    emitBattleEvent("deck-radar-cancel")
    navigate("/play/deck-builder")
  }

  return (
    <GameScreenLayout title="Поиск колодного боя" backTo="/play/deck-builder">
      <p className="text-sm text-guardians-muted">
        {formatDeckSearchModeLabel(deckSearchMode, colorRank)}
      </p>
      <p className="mt-2 text-sm">{status}</p>
      <div className="mt-4 flex gap-2">
        <button
          type="button"
          disabled={searching || cardIds.length !== 6}
          onClick={search}
          className="rounded-xl bg-guardians-accent px-4 py-2 text-sm text-guardians-bg disabled:opacity-50"
        >
          {searching ? "…" : "Искать"}
        </button>
        <button
          type="button"
          onClick={cancel}
          className="rounded-xl border border-white/20 px-4 py-2 text-sm"
        >
          Отмена
        </button>
      </div>
    </GameScreenLayout>
  )
}
